import Admission from "./pages/Admission";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { SiteSettingsProvider } from "./contexts/SiteSettingsContext";
import Home from "./pages/HomePage";
import About from "./pages/About";
import Mission from "./pages/Mission";
import Team from "./pages/team";
import Terms from "./pages/policy";
import InfoTech from "./pages/InformationTechnology";
// Universal Program Detail Page (used for short-courses / professional-courses subjects)
import UniversalProgramDetailPage from "./components/ProgramDetail/UniversalProgramDetailPage";
// Keep old imports for backward compatibility (can be removed later)
import CyberSec from "./pages/CyberSecurity";
import BusinessAdministration from "./pages/BusinessAdministration";
import TravelTourismManagement from "./pages/TravelTourismManagement";
import ShortCourses from "./pages/ShortCourses";
import PhotoGallery from "./pages/PhotoGallery";
import VideoGallery from "./pages/VideoGallery";
import AllEvents from "./pages/AllEvents";
import EventDetail from "./pages/EventDetail";
import AllNews from "./pages/AllNews";
import Apply from "./pages/Apply";
import StudentPortal from "./pages/StudentPortal";
import NotFound from "./pages/NotFound";
import NewsDetail from "./pages/NewsDetail";
import ContactUs from "./pages/ContactUs";
import FeeStructure from "./pages/FeeStructure";
import Athletics from "./pages/Athletics";
import AthleticsNews from "./pages/AthleticsNews";
import CampusTour from "./pages/CampusTour";
// HIDDEN 2026-08-04 at client request — unhide by restoring these imports along with the routes below.
// import Academics from "./pages/Academics";
// import Departments from "./pages/Departments";
// HIDDEN 2026-08-04 at client request — unhide by restoring this import along with the route below.
// import Library from "./pages/Library";
import Services from "./pages/Services";
import JrMediaProductions from "./pages/JrMediaProductions";
import DataAnalysisServices from "./pages/DataAnalysisServices";
import Feedback from "./pages/Feedback";
import ProfessionalCourses from "./pages/ProfessionalCourses";
import CompTIADetailPage from "./pages/CompTIADetailPage";
import CompTIACertDetailPage from "./pages/CompTIACertDetailPage";
import CiscoDetailPage from "./pages/CiscoDetailPage";
import CiscoCareerPathPage from "./pages/CiscoCareerPathPage";
import CiscoCourseDetailPage from "./pages/CiscoCourseDetailPage";
import ECCouncilDetailPage from "./pages/ECCouncilDetailPage";
import DynamicCoursePage from "./pages/DynamicCoursePage";
import ScrollToTopButton from "./components/Home/ScrollButton";
//import Blog from "./pages/Blog";

// Scroll to top on route change
import { useLayoutEffect } from "react";
import { useLocation, useParams } from "react-router-dom";

// Courses whose subject detail pages have been removed.
// Any /courses/<slug>/:slug URL for these is treated as not-found.
const SUBJECTLESS_COURSES = new Set([
  "information-technology",
  "cyber-security",
  "business-administration",
  "travel-tourism-management",
  "short-courses",
]);

function DynamicCourseProgram() {
  const { courseSlug } = useParams();
  if (SUBJECTLESS_COURSES.has(courseSlug)) {
    return <NotFound />;
  }
  return <UniversalProgramDetailPage courseCategory={courseSlug} />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function App() {
  return (
    <SiteSettingsProvider>
    <Router>
      <ScrollToTop />
      <ScrollToTopButton />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/about/mission" element={<Mission />} />
        <Route path="/about/trustees" element={<Team />} />
        <Route path="/about/terms-conditions" element={<Terms />} />
        <Route path="/course/information-technology" element={<InfoTech />} />
        <Route path="/course/cyber-security" element={<CyberSec />} />
        <Route
          path="/course/business-administration"
          element={<BusinessAdministration />}
        />
        <Route
          path="/course/travel-tourism-management"
          element={<TravelTourismManagement />}
        />
        <Route path="/course/short-courses" element={<ShortCourses />} />
        {/* Subject pages were merged into the main Short Courses page (each subject
            now has its own Overview/Courses tabs there) — redirect any old bookmarked
            or indexed subject-page links back to it instead of 404ing. */}
        <Route path="/course/short-courses/:categorySlug" element={<Navigate to="/course/short-courses" replace />} />
        <Route
          path="/course/professional-courses"
          element={<ProfessionalCourses />}
        />
        <Route path="/gallery/photos" element={<PhotoGallery />} />
        <Route path="/gallery/videos" element={<VideoGallery />} />
        <Route path="/events" element={<AllEvents />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/news" element={<AllNews />} />
        <Route path="/news/:id" element={<NewsDetail />} />
        <Route path="/apply" element={<Apply />} />
        <Route path="/student-portal" element={<StudentPortal />} />
        <Route path="/contact" element={<ContactUs />} />
        <Route path="/fees" element={<FeeStructure />} />
        <Route path="/athletics" element={<Athletics />} />
        <Route path="/athletics/news" element={<AthleticsNews />} />
        <Route path="/campus-tour" element={<CampusTour />} />
        {/* HIDDEN 2026-08-04 at client request — unhide by restoring these two routes.
            <Route path="/academics" element={<Academics />} />
            <Route path="/departments" element={<Departments />} />
        */}
        {/* /programs used to redirect to /academics; falls through to NotFound while academics is hidden */}
        {/* HIDDEN 2026-08-04 at client request — unhide by restoring this route.
            <Route path="/library" element={<Library />} />
        */}
        <Route path="/services" element={<Services />} />
        <Route path="/services/jr-media-productions" element={<JrMediaProductions />} />
        <Route path="/services/data-analysis" element={<DataAnalysisServices />} />
        <Route path="/feedback" element={<Feedback />} />
        <Route path="/admissions" element={<Admission />} />
        {/*        <Route path="/blog" element={<Blog />} />*/}

        {/* Dynamic catch-all for new courses added via CMS */}
        <Route path="/course/:slug" element={<DynamicCoursePage />} />

        {/* Professional course detail pages — must appear before the dynamic catch-all */}
        <Route path="/courses/professional-courses/comptia" element={<CompTIADetailPage />} />
        <Route path="/courses/professional-courses/comptia/:certSlug" element={<CompTIACertDetailPage />} />
        <Route path="/courses/professional-courses/cisco-networking-academy" element={<CiscoDetailPage />} />
        <Route path="/courses/professional-courses/cisco-networking-academy/:pathSlug" element={<CiscoCareerPathPage />} />
        <Route path="/courses/professional-courses/cisco-networking-academy/:pathSlug/:courseSlug" element={<CiscoCourseDetailPage />} />
        <Route path="/courses/professional-courses/ec-council" element={<ECCouncilDetailPage />} />

        {/* Dynamic catch-all for program detail pages under any course */}
        <Route
          path="/courses/:courseSlug/:slug"
          element={<DynamicCourseProgram />}
        />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </Router>
    </SiteSettingsProvider>
  );
}
export default App;
